import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link2, Check, Bookmark, BookmarkCheck, Share2, X } from "lucide-react";
import { useBookmarks } from "../hooks/useBookmarks";
import { track } from "../lib/analytics";

/**
 * ShareDock — The Context Project's distribution engine.
 *
 * Behaviour:
 *  • Desktop: low-opacity floating glass dock on the right edge, midway down.
 *    Fades in after the reader scrolls ≥ 200px (engagement signal).
 *    Auto-collapses to a 1-icon strip after 6s of inactivity; expand on hover.
 *  • Mobile: bottom-floating bar with safe-area-inset padding. Auto-hides on
 *    scroll-down, reappears on scroll-up.
 *  • Native Web Share API is tried first on touch devices — system sheet feels
 *    premium; falls back to the dock if user dismisses.
 *
 * Props are intentionally minimal:
 *   title:       Used as the share text (and tab title for native share)
 *   description: Optional secondary text used by some destinations
 *   url:         Canonical share URL — defaults to current location
 *   type:        'topic' | 'entity' | 'event' | 'source' (for bookmark + analytics)
 *   id:          Stable identifier for bookmark + analytics
 *   anchor:      Optional URL fragment (e.g. '#source-3') — future-compatible
 *                with evidence cards, quote blocks, timeline anchors, etc.
 *
 * Pure share URLs — no third-party SDKs loaded. Calm by design.
 */

const SITE_URL = process.env.REACT_APP_PUBLIC_SITE_URL || (typeof window !== "undefined" ? window.location.origin : "");

// Lightweight SVG glyphs for the social destinations. Single-path icons keep
// the dock visually quiet and reduce bundle weight (no extra icon imports).
const IconFb = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-[18px] h-[18px]"><path d="M13.5 21v-8.25H16l.375-3H13.5V7.875c0-.84.234-1.41 1.453-1.41h1.547v-2.7A21 21 0 0 0 14.156 3.6c-2.273 0-3.844 1.387-3.844 3.937v2.213H7.5v3h2.812V21z" /></svg>
);
const IconX = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-[17px] h-[17px]"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
);
const IconWA = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-[18px] h-[18px]"><path d="M19.05 4.91A9.82 9.82 0 0 0 12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.9 9.9 0 0 0 4.74 1.21h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.91-7.01M12.04 20.15h-.01a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.2 8.2 0 0 1-1.26-4.38c0-4.54 3.7-8.23 8.25-8.23a8.23 8.23 0 0 1 8.23 8.24c0 4.54-3.7 8.23-8.23 8.23m4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.12s-.64.81-.79.97-.29.18-.54.06-1.05-.39-2-1.23a7.6 7.6 0 0 1-1.39-1.73c-.14-.25-.02-.38.11-.5.11-.11.25-.29.37-.43.12-.14.16-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31s-.86.85-.86 2.07.88 2.4 1.01 2.57c.12.16 1.73 2.65 4.19 3.71.59.25 1.05.41 1.4.52.59.19 1.12.16 1.55.1.47-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.15-1.18-.06-.11-.23-.18-.48-.3" /></svg>
);
const IconTG = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-[18px] h-[18px]"><path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42" /></svg>
);

function buildShareUrls({ url, title, description }) {
    const u = encodeURIComponent(url);
    // Subtle attribution baked into every shared headline — turns each
    // organic share into a discovery signal back to the platform.
    const titleWithAttribution = title ? `${title} — The Context Project` : "The Context Project";
    const t = encodeURIComponent(titleWithAttribution);
    const td = encodeURIComponent([titleWithAttribution, description].filter(Boolean).join(" — "));
    // X / Twitter intent — add via @ handle for natural attribution.
    const x_text = encodeURIComponent(`${titleWithAttribution}\n\nvia @thecontextproj`);
    return {
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
        x: `https://twitter.com/intent/tweet?text=${x_text}&url=${u}`,
        whatsapp: `https://wa.me/?text=${td}%20${u}`,
        telegram: `https://t.me/share/url?url=${u}&text=${t}`,
    };
}

function useScrollEngagement(threshold = 200) {
    const [engaged, setEngaged] = useState(false);
    useEffect(() => {
        const onScroll = () => { if (window.scrollY > threshold) setEngaged(true); };
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, [threshold]);
    return engaged;
}

function useMobileVisibility() {
    // Show by default. Hide when scrolling down, reveal when scrolling up.
    const [visible, setVisible] = useState(true);
    const lastY = useRef(typeof window !== "undefined" ? window.scrollY : 0);
    useEffect(() => {
        const onScroll = () => {
            const y = window.scrollY;
            const dy = y - lastY.current;
            if (Math.abs(dy) < 6) return;
            setVisible(dy < 0 || y < 80);
            lastY.current = y;
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);
    return visible;
}

export default function ShareDock({ title, description, url, type, id, anchor }) {
    const engaged = useScrollEngagement(200);
    const mobileVisible = useMobileVisibility();
    const { isBookmarked, toggle } = useBookmarks();
    const [copied, setCopied] = useState(false);
    const [expanded, setExpanded] = useState(true);
    const collapseTimer = useRef(null);

    const finalUrl = useMemo(() => {
        const base = url || (typeof window !== "undefined" ? `${SITE_URL}${window.location.pathname}${window.location.search}` : "");
        return anchor ? `${base}${anchor.startsWith("#") ? "" : "#"}${anchor}` : base;
    }, [url, anchor]);

    const shareUrls = useMemo(() => buildShareUrls({ url: finalUrl, title, description }), [finalUrl, title, description]);
    const bookmarked = type && id ? isBookmarked(type, id) : false;

    // Auto-collapse after 6s of inactivity on desktop
    const scheduleCollapse = () => {
        if (collapseTimer.current) clearTimeout(collapseTimer.current);
        collapseTimer.current = setTimeout(() => setExpanded(false), 6000);
    };
    useEffect(() => {
        if (engaged) {
            setExpanded(true);
            scheduleCollapse();
        }
        return () => { if (collapseTimer.current) clearTimeout(collapseTimer.current); };
    }, [engaged]);

    const onShare = (destination, href) => {
        track("tcp_share", { destination, content_type: type, content_id: id, url: finalUrl });
        if (destination === "native") {
            navigator.share({ title, text: description, url: finalUrl }).catch(() => {});
            return;
        }
        if (href) window.open(href, "_blank", "noopener,noreferrer");
    };

    const onCopy = async () => {
        try {
            await navigator.clipboard.writeText(finalUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
            track("tcp_share", { destination: "copy", content_type: type, content_id: id, url: finalUrl });
        } catch {
            /* clipboard denied — silent */
        }
    };

    const onBookmark = () => {
        if (!type || !id) return;
        const added = toggle({
            type, id, title, subtitle: description, href: finalUrl,
        });
        track("tcp_bookmark", { action: added ? "add" : "remove", type, id, source: "share_dock" });
    };

    const tryNativeShare = async () => {
        if (typeof navigator !== "undefined" && navigator.share) {
            try { onShare("native"); return true; } catch { /* fallthrough */ }
        }
        return false;
    };

    // ------- Render -------

    // Desktop vertical dock: fixed right edge, mid-page, glass-morphism, fade-in
    // after engagement. Collapses to a tiny strip on inactivity, expands on hover.
    const dockBase = "fixed z-40 top-1/2 -translate-y-1/2 right-3 hidden lg:flex flex-col items-center gap-1 p-2 rounded-2xl border border-[#243044]/70 bg-[#0B1220]/65 backdrop-blur-xl shadow-[0_8px_40px_-12px_rgba(0,0,0,0.6)] transition-all duration-500";
    const dockState = engaged ? "opacity-90" : "opacity-0 pointer-events-none translate-x-4";

    const itemBase = "group relative w-9 h-9 rounded-lg flex items-center justify-center text-[#94A3B8] hover:text-[#56CCF2] hover:bg-[#2F80ED]/10 hover:shadow-[0_0_18px_rgba(86,204,242,0.18)] transition-all duration-200";

    // Mobile bar
    const mobileBase = "fixed z-40 left-3 right-3 bottom-3 lg:hidden flex items-center justify-between px-2 py-2 rounded-2xl border border-[#243044]/70 bg-[#0B1220]/85 backdrop-blur-xl shadow-[0_8px_40px_-12px_rgba(0,0,0,0.8)] transition-all duration-300";
    const mobileState = mobileVisible ? "translate-y-0 opacity-100" : "translate-y-[120%] opacity-0 pointer-events-none";

    return (
        <>
            {/* Desktop right-edge dock */}
            <div
                data-testid="share-dock-desktop"
                data-expanded={expanded}
                onMouseEnter={() => { setExpanded(true); if (collapseTimer.current) clearTimeout(collapseTimer.current); }}
                onMouseLeave={scheduleCollapse}
                className={`${dockBase} ${dockState} hover:opacity-100`}
                aria-label="Share this investigation"
            >
                <div className="text-[9px] uppercase tracking-[0.2em] text-[#56CCF2]/80 px-1 pb-1 select-none">
                    {expanded ? "Share" : <Share2 className="w-3 h-3" />}
                </div>
                <div className={`flex flex-col items-center gap-1 transition-all duration-300 overflow-hidden ${expanded ? "max-h-[400px] opacity-100" : "max-h-0 opacity-0"}`}>
                    <button data-testid="share-facebook" onClick={() => onShare("facebook", shareUrls.facebook)} className={itemBase} title="Share to Facebook"><IconFb /></button>
                    <button data-testid="share-x" onClick={() => onShare("x", shareUrls.x)} className={itemBase} title="Share to X"><IconX /></button>
                    <button data-testid="share-whatsapp" onClick={() => onShare("whatsapp", shareUrls.whatsapp)} className={itemBase} title="Share to WhatsApp"><IconWA /></button>
                    <button data-testid="share-telegram" onClick={() => onShare("telegram", shareUrls.telegram)} className={itemBase} title="Share to Telegram"><IconTG /></button>
                    <div className="w-6 h-px bg-[#243044]/70 my-1" />
                    <button data-testid="share-copy" onClick={onCopy} className={itemBase} title={copied ? "Copied!" : "Copy link"}>
                        {copied ? <Check className="w-[18px] h-[18px] text-[#56CCF2]" /> : <Link2 className="w-[18px] h-[18px]" />}
                    </button>
                    {type && id && (
                        <button data-testid="share-bookmark" onClick={onBookmark} className={itemBase} title={bookmarked ? "Saved — click to remove" : "Save for later"}>
                            {bookmarked ? <BookmarkCheck className="w-[18px] h-[18px] text-[#56CCF2]" /> : <Bookmark className="w-[18px] h-[18px]" />}
                        </button>
                    )}
                </div>
            </div>

            {/* Mobile bottom bar */}
            <div
                data-testid="share-dock-mobile"
                className={`${mobileBase} ${mobileState}`}
                style={{ paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))" }}
            >
                <button
                    data-testid="share-mobile-native"
                    onClick={async () => { if (!(await tryNativeShare())) {/* dock is already visible on mobile */} }}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs uppercase tracking-wider font-medium text-[#56CCF2] hover:bg-[#2F80ED]/10 transition-colors"
                >
                    <Share2 className="w-4 h-4" /> Share
                </button>
                <div className="flex items-center gap-0.5">
                    <button data-testid="share-mobile-whatsapp" onClick={() => onShare("whatsapp", shareUrls.whatsapp)} className="w-10 h-10 flex items-center justify-center text-[#94A3B8] hover:text-[#56CCF2] active:bg-[#2F80ED]/10 rounded-xl"><IconWA /></button>
                    <button data-testid="share-mobile-x" onClick={() => onShare("x", shareUrls.x)} className="w-10 h-10 flex items-center justify-center text-[#94A3B8] hover:text-[#56CCF2] active:bg-[#2F80ED]/10 rounded-xl"><IconX /></button>
                    <button data-testid="share-mobile-facebook" onClick={() => onShare("facebook", shareUrls.facebook)} className="w-10 h-10 flex items-center justify-center text-[#94A3B8] hover:text-[#56CCF2] active:bg-[#2F80ED]/10 rounded-xl"><IconFb /></button>
                    <button data-testid="share-mobile-telegram" onClick={() => onShare("telegram", shareUrls.telegram)} className="w-10 h-10 flex items-center justify-center text-[#94A3B8] hover:text-[#56CCF2] active:bg-[#2F80ED]/10 rounded-xl"><IconTG /></button>
                    <div className="w-px h-5 bg-[#243044]/80 mx-1" />
                    <button data-testid="share-mobile-copy" onClick={onCopy} className="w-10 h-10 flex items-center justify-center text-[#94A3B8] hover:text-[#56CCF2] active:bg-[#2F80ED]/10 rounded-xl">
                        {copied ? <Check className="w-[18px] h-[18px] text-[#56CCF2]" /> : <Link2 className="w-[18px] h-[18px]" />}
                    </button>
                    {type && id && (
                        <button data-testid="share-mobile-bookmark" onClick={onBookmark} className="w-10 h-10 flex items-center justify-center text-[#94A3B8] hover:text-[#56CCF2] active:bg-[#2F80ED]/10 rounded-xl">
                            {bookmarked ? <BookmarkCheck className="w-[18px] h-[18px] text-[#56CCF2]" /> : <Bookmark className="w-[18px] h-[18px]" />}
                        </button>
                    )}
                </div>
            </div>

            {/* Brief copied confirmation toast (subtle, no library) */}
            {copied && (
                <div data-testid="share-copied-toast" className="fixed z-50 bottom-20 lg:bottom-6 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-[#0B1220]/95 border border-[#56CCF2]/40 backdrop-blur-xl text-xs uppercase tracking-wider text-[#56CCF2] flex items-center gap-2 pointer-events-none">
                    <Check className="w-3.5 h-3.5" /> Link copied
                </div>
            )}
        </>
    );
}
